/**
 * ChatSidebar
 * Lists conversations across 4 tabs: All, Rituality AI, Rituals, People.
 * Includes search, archive view, and new conversation actions.
 */

import {
  Archive,
  ArrowLeft,
  EllipsisVertical,
  PanelLeftClose,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react"
import { useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import aiAvatarImg from "@/assets/icons/ai-conversation-avatar.png"
import peopleIcon from "@/assets/icons/people.svg"
import ritualityAiIcon from "@/assets/icons/rituality-ai.svg"
import ritualsLinkIcon from "@/assets/icons/rituals-link.svg"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  useAiConversations,
  useCreateAiConversation,
  useDeleteAiConversation,
  useUpdateAiConversation,
} from "../../hooks/useAiChat"
import { useDeleteDmConversation, useDmConversations } from "../../hooks/useDmChat"
import { useRitualGroupChats } from "../../hooks/useRitualGroupChat"
import { useAllConversations, useArchiveConversation } from "../../hooks/useUnifiedConversations"
import type { AiConversation } from "../../types/ai-chat.types"
import type { DmConversation } from "../../types/dm-chat.types"
import type { RitualGroupConversationListItem } from "../../types/ritual-group-chat.types"
import { formatTimeAgo } from "../../utils/formatTimeAgo"
import { UserSearchModal } from "../dm/UserSearchModal"
import { ArchiveFilledIcon } from "./ArchiveFilledIcon"
import { ArchiveView } from "./ArchiveView"
import { ConversationListItem } from "./ConversationListItem"
import { ConversationSearch } from "./ConversationSearch"
import { UnreadBadge } from "./UnreadBadge"

type TabType = "all" | "ai" | "rituals" | "people"

interface TabDefinition {
  key: TabType
  label: string
  icon?: string
}

const TABS: TabDefinition[] = [
  { key: "all", label: "All" },
  { key: "ai", label: "Rituality AI", icon: ritualityAiIcon },
  { key: "rituals", label: "Rituals", icon: ritualsLinkIcon },
  { key: "people", label: "People", icon: peopleIcon },
]

function AiConversationItem({ conversation }: { conversation: AiConversation }) {
  const navigate = useNavigate()
  const { id } = useParams()
  const isActive = id === conversation.id
  const isReflect = conversation.metadata?.type === "reflect"
  const deleteConversation = useDeleteAiConversation()
  const updateConversation = useUpdateAiConversation()

  const handleArchive = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    updateConversation.mutate(
      { id: conversation.id, data: { status: "archived" } },
      {
        onSuccess: () => {
          toast.success("Conversation archived")
          if (isActive) navigate("/chats")
        },
        onError: () => toast.error("Failed to archive conversation"),
      },
    )
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    deleteConversation.mutate(conversation.id, {
      onSuccess: () => {
        toast.success("Conversation deleted")
        if (isActive) navigate("/chats")
      },
      onError: () => toast.error("Failed to delete conversation"),
    })
  }

  return (
    <Link
      to={`/chats/ai/${conversation.id}`}
      className={cn(
        "group relative w-full flex flex-col gap-[10px] px-[20px] py-[15px] text-left transition-colors no-underline border-b border-border",
        isActive ? "bg-[var(--color-chat-active)]" : "hover:bg-accent",
      )}
    >
      {/* Top row: avatar + name + time/actions */}
      <div className="flex items-center h-[30px]">
        <div className="flex items-center gap-[10px] flex-1 min-w-0">
          <img src={aiAvatarImg} alt="Rituality AI" className="size-[30px] rounded-full shrink-0" />
          <div className="flex items-center gap-1 min-w-0">
            {isReflect && <Sparkles className="size-3.5 shrink-0 text-amber-400" />}
            <p className="text-[13px] font-semibold text-foreground truncate">
              {conversation.title || "New Chat"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-[13px] text-muted-foreground/70 group-hover:hidden">
            {formatTimeAgo(conversation.lastMessageAt || conversation.createdAt)}
          </span>
          <div className="hidden group-hover:flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleArchive}
              className="hover:bg-muted text-muted-foreground"
              title="Archive"
            >
              <Archive className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleDelete}
              className="hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
              title="Delete"
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Message preview */}
      <p className="text-[13px] font-normal text-foreground leading-[25px] line-clamp-2">
        {conversation.lastMessagePreview || "Start a conversation..."}
      </p>

      {/* Type badge */}
      <div className="flex items-center gap-[5px]">
        <img src={ritualityAiIcon} alt="" className="size-[14px]" />
        <span className="text-[13px] font-semibold text-[var(--color-chat-badge)]">
          Rituality AI
        </span>
      </div>
    </Link>
  )
}

function DmConversationItem({ conversation }: { conversation: DmConversation }) {
  const navigate = useNavigate()
  const { conversationId } = useParams()
  const isActive = conversationId === conversation.id
  const deleteConversation = useDeleteDmConversation()
  const archiveConversation = useArchiveConversation()

  const handleArchive = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    archiveConversation.mutate(conversation.id, {
      onSuccess: () => {
        toast.success("Conversation archived")
        if (isActive) navigate("/chats")
      },
      onError: () => toast.error("Failed to archive conversation"),
    })
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    deleteConversation.mutate(conversation.id, {
      onSuccess: () => {
        toast.success("Conversation deleted")
        if (isActive) {
          navigate("/chats")
        }
      },
      onError: () => {
        toast.error("Failed to delete conversation")
      },
    })
  }

  const otherUser = conversation.otherUser

  return (
    <Link
      to={`/chats/dm/${conversation.id}`}
      className={cn(
        "group w-full flex items-center gap-3 px-4 py-3 text-left transition-colors no-underline border-b border-border",
        isActive ? "bg-[var(--color-chat-active)]" : "hover:bg-accent",
      )}
    >
      <Avatar
        src={otherUser?.profilePhoto || undefined}
        alt={otherUser?.name || "User"}
        fallback={otherUser?.name || "?"}
        size="md"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] font-semibold text-foreground truncate">
            {otherUser?.name || "Unknown user"}
            {otherUser?.username && (
              <span className="font-normal text-muted-foreground ml-1">@{otherUser.username}</span>
            )}
          </p>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-[13px] text-muted-foreground/70 group-hover:hidden">
              {formatTimeAgo(conversation.lastMessageAt)}
            </span>
            <div className="hidden group-hover:flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={handleArchive}
                className="hover:bg-muted text-muted-foreground"
                title="Archive"
              >
                <Archive className="size-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={handleDelete}
                className="hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                title="Delete"
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] text-muted-foreground truncate">
            {conversation.lastMessage?.content || "No messages yet"}
          </p>
          <UnreadBadge count={conversation.unreadCount} />
        </div>
      </div>
    </Link>
  )
}

function RitualGroupItem({ conversation }: { conversation: RitualGroupConversationListItem }) {
  const location = useLocation()
  const route = `/chats/ritual/${conversation.id}`
  const isActive = location.pathname === route
  // Three flavors share this row type — the subtitle has to disambiguate
  // them. The booking presence flips the row from "organizer chat" to
  // "expert chat"; the anchor decides whether the expert chat is for a
  // coaching session or a ritual booking.
  const isExpertChat = conversation.bookingId != null
  const subtitle = isExpertChat
    ? conversation.coachingSessionId != null
      ? "Coaching Session"
      : "Ritual Session"
    : `${conversation.participantCount} organizers`

  return (
    <Link
      to={route}
      className={cn(
        "group w-full flex items-center gap-3 px-4 py-3 text-left transition-colors no-underline border-b border-border",
        isActive ? "bg-[var(--color-chat-active)]" : "hover:bg-accent",
      )}
    >
      {conversation.profilePhoto ? (
        <img
          src={conversation.profilePhoto}
          alt=""
          className="size-[30px] rounded-full object-cover shrink-0"
        />
      ) : (
        <div className="size-[30px] rounded-full bg-primary/20 flex items-center justify-center shrink-0">
          <img src={ritualsLinkIcon} alt="" className="size-4 opacity-70" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] font-semibold text-foreground truncate">{conversation.name}</p>
          <span className="text-[13px] text-muted-foreground/40 shrink-0">
            {formatTimeAgo(conversation.lastMessageAt)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] text-muted-foreground truncate">{subtitle}</p>
          <UnreadBadge count={conversation.unreadCount} />
        </div>
      </div>
    </Link>
  )
}

function detectActiveTab(pathname: string): TabType {
  if (pathname.includes("/chats/dm")) return "people"
  if (pathname.includes("/chats/ritual")) return "rituals"
  if (pathname.includes("/chats/ai")) return "ai"
  return "all"
}

export function ChatSidebar({ onCollapse }: { onCollapse?: () => void }) {
  const navigate = useNavigate()
  const location = useLocation()

  const [activeTab, setActiveTab] = useState<TabType>(detectActiveTab(location.pathname))
  const [showUserSearch, setShowUserSearch] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [showArchive, setShowArchive] = useState(false)

  const aiData = useAiConversations()
  const dmData = useDmConversations()
  const ritualGroupData = useRitualGroupChats()
  const allData = useAllConversations()
  const createAiConversation = useCreateAiConversation()

  const handleNewChat = async () => {
    if (activeTab === "people") {
      setShowUserSearch(true)
      return
    }

    // Reuse the most recent empty conversation if one exists so users don't
    // accumulate untitled stubs from repeated New Chat clicks.
    const existingEmpty = aiData.data?.conversations.find(
      c => c.title === null && c.lastMessagePreview === null && c.lastMessageAt === null,
    )
    if (existingEmpty) {
      navigate(`/chats/ai/${existingEmpty.id}`)
      return
    }

    try {
      const conversation = await createAiConversation.mutateAsync()
      navigate(`/chats/ai/${conversation.id}`)
    } catch {
      toast.error("Failed to create conversation")
    }
  }

  const isLoading =
    activeTab === "all"
      ? allData.isLoading
      : activeTab === "ai"
        ? aiData.isLoading
        : activeTab === "rituals"
          ? // Rituals tab merges ritual group chats with ritual-scoped DMs,
            // so wait for both sources before deciding if the list is empty.
            ritualGroupData.isLoading || dmData.isLoading
          : // People tab filters out ritual-scoped DMs; it still only needs
            // dmData, but we also want any unread counts to match — a single
            // source is sufficient here.
            dmData.isLoading

  return (
    <>
      <div className="w-full md:w-[375px] shrink-0 border-r border-border min-[1445px]:bg-[#FFFFFF0D] flex flex-col h-full">
        {/* Header - matches Figma: + New Chat left, icons right */}
        <div className="h-14 px-4 flex items-center justify-between border-b border-border">
          {showArchive ? (
            <button
              type="button"
              onClick={() => setShowArchive(false)}
              className="flex items-center gap-1.5 text-foreground hover:opacity-80 transition-opacity"
            >
              <ArrowLeft className="size-5" />
              <span className="text-[13px] font-semibold">Archive</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNewChat}
              disabled={createAiConversation.isPending}
              className="flex items-center gap-1.5 text-foreground hover:opacity-80 transition-opacity"
            >
              <Plus className="size-5" />
              <span className="text-[13px] font-semibold">New Chat</span>
            </button>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSearch(true)}
              className="text-foreground hover:opacity-70 transition-opacity"
              title="Search conversations"
            >
              <Search className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => setShowArchive(!showArchive)}
              className="text-foreground hover:opacity-70 transition-opacity"
              title="Archived conversations"
            >
              {showArchive ? (
                <ArchiveFilledIcon className="size-5" />
              ) : (
                <Archive className="size-5" />
              )}
            </button>
            {onCollapse && (
              <button
                type="button"
                onClick={onCollapse}
                className="text-foreground hover:opacity-70 transition-opacity"
                title="Collapse sidebar"
              >
                <PanelLeftClose className="size-5 hidden md:block" />
                <EllipsisVertical className="size-5 md:hidden" />
              </button>
            )}
          </div>
        </div>

        {/* Tabs - with icons per Figma */}
        {!showArchive && (
          <div className="flex h-[50px]">
            {TABS.map(tab => {
              const isActive = activeTab === tab.key
              const isAll = tab.key === "all"
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    "flex items-center justify-center gap-[5px] h-[50px] text-[13px] whitespace-nowrap transition-colors",
                    isAll ? "w-[48px] shrink-0" : "flex-1",
                    isActive
                      ? "text-foreground font-semibold border-b border-foreground bg-[var(--color-chat-tab-active-bg)]"
                      : "text-muted-foreground font-normal border-b border-border hover:opacity-70",
                  )}
                >
                  {tab.icon && (
                    <img
                      src={tab.icon}
                      alt=""
                      className={cn("size-[20px] shrink-0", !isActive && "opacity-40")}
                    />
                  )}
                  {tab.label}
                </button>
              )
            })}
          </div>
        )}

        {/* Search bar (inline below tabs) */}
        {showSearch && <ConversationSearch onClose={() => setShowSearch(false)} />}

        {/* Archive view (inline below tabs) */}
        {showArchive && !showSearch && <ArchiveView />}

        {/* Conversation list (hidden when searching or viewing archive) */}
        {!showSearch && !showArchive && (
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="size-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : activeTab === "all" ? (
              allData.data?.conversations.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">No conversations yet</p>
                </div>
              ) : (
                allData.data?.conversations.map(conversation => (
                  <ConversationListItem
                    key={`${conversation.type}-${conversation.id}`}
                    conversation={conversation}
                    showTypeBadge
                  />
                ))
              )
            ) : activeTab === "ai" ? (
              aiData.data?.conversations.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">No conversations yet</p>
                </div>
              ) : (
                aiData.data?.conversations.map(conversation => (
                  <AiConversationItem key={conversation.id} conversation={conversation} />
                ))
              )
            ) : activeTab === "rituals" ? (
              (() => {
                // Rituals tab includes both organizer group chats and any
                // ritual-scoped DMs (e.g. expert chats from booked sessions).
                const ritualGroupItems = ritualGroupData.data?.conversations ?? []
                const ritualScopedDms = (dmData.data?.conversations ?? []).filter(
                  c => c.ritual !== null && c.ritual !== undefined,
                )
                if (ritualGroupItems.length === 0 && ritualScopedDms.length === 0) {
                  return (
                    <div className="text-center py-8 px-4">
                      <p className="text-sm text-muted-foreground">No ritual chats yet</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        Group chats appear when a ritual organizer creates one. Booked coaching
                        sessions show up here too.
                      </p>
                    </div>
                  )
                }
                return (
                  <>
                    {ritualGroupItems.map(conversation => (
                      <RitualGroupItem key={conversation.id} conversation={conversation} />
                    ))}
                    {ritualScopedDms.map(conversation => (
                      <DmConversationItem key={conversation.id} conversation={conversation} />
                    ))}
                  </>
                )
              })()
            ) : (
              (() => {
                // People tab: plain DMs only. Ritual-scoped DMs live under Rituals.
                const peopleDms = (dmData.data?.conversations ?? []).filter(c => !c.ritual)
                if (peopleDms.length === 0) {
                  return (
                    <div className="text-center py-8 px-4">
                      <p className="text-sm text-muted-foreground">No messages yet</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        Visit a profile and tap Chat to start a conversation
                      </p>
                    </div>
                  )
                }
                return peopleDms.map(conversation => (
                  <DmConversationItem key={conversation.id} conversation={conversation} />
                ))
              })()
            )}
          </div>
        )}
      </div>

      <UserSearchModal open={showUserSearch} onOpenChange={setShowUserSearch} />
    </>
  )
}
